import { useMemo } from 'react';
import { t } from '@lingui/macro';

import { useId } from '../../page-framework/panel/Panel';
import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import PanelActions from '../../page-framework/panel/PanelActions';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import ChartTypeForm from '../../charts/ipm/ChartTypeForm';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { interfaceFilterId, timeRangeFilterId } from '../../filters';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';

const title = t`Interface Utilization`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'interface',
        metrics: [
            'bytesIn',
            'bytesOut',
            'inBitsPerSec.avg',
            'outBitsPerSec.avg',
            'avgNetworkIn',
            'avgNetworkOut',
            'statusInterfaceUtil',
        ],
    },
};

const RATE = 'RATE';
const VOLUME = 'VOLUME';
const PERCENT = 'PERCENT';

const typeConfig = {
    [RATE]: {
        formatter: FORMATTER_TYPES.BITRATE,
        yAxisTitle: t`Utilization (Bit/s)`,
        units: undefined,
        inMetric: 'inBitsPerSec.avg',
        outMetric: 'outBitsPerSec.avg',
    },
    [VOLUME]: {
        formatter: FORMATTER_TYPES.BYTERATE,
        yAxisTitle: t`Utilization (Bytes)`,
        units: undefined,
        inMetric: 'bytesIn',
        outMetric: 'bytesOut',
    },
    [PERCENT]: {
        formatter: FORMATTER_TYPES.PERCENT,
        forceDecimals: true,
        yAxisTitle: t`Utilization (%)`,
        units: '%',
        inMetric: 'avgNetworkIn',
        outMetric: 'avgNetworkOut',
    },
};

function useChartConfig(type) {
    const inColor = useMetricColor('networkIn');
    const outColor = useMetricColor('networkOut');
    const config = typeConfig[type];

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: config.yAxisTitle,
                    },
                },
            ],
            tooltip: BuiltInTooltip,
            showGranularity: true,
            statusMetric: 'statusInterfaceUtil',
            chartTrends: [
                {
                    formatter: config.formatter,
                    units: config.units,
                    chartType: 'spline',
                    key: config.inMetric,
                    label: t`In`,
                    color: inColor,
                    multiplier: 1,
                    forceDecimals: config.forceDecimals === true,
                },
                {
                    formatter: config.formatter,
                    units: config.units,
                    chartType: 'spline',
                    key: config.outMetric,
                    label: t`Out`,
                    color: outColor,
                    multiplier: 1,
                    forceDecimals: config.forceDecimals === true,
                },
            ],
        }),
        [config, inColor, outColor]
    );
}

const selections = [
    { value: RATE, label: t`Rate` },
    { value: VOLUME, label: t`Volume` },
    { value: PERCENT, label: '%' },
];

function InterfaceUtilizationPanel() {
    const id = useId();
    const pageStateKey = `${id}-ChartType`;
    const [chartDisplayType, setChartDisplayType] = useChartDisplayType(pageStateKey, PERCENT);
    const chartConfig = useChartConfig(chartDisplayType);
    const data = usePanelDataService(dataSource);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return (
        <>
            <PanelActions>
                <ChartTypeForm value={chartDisplayType} onChangeType={setChartDisplayType} selections={selections} />
            </PanelActions>
            <PerformanceChart chartDef={chartConfig} data={chartData} />
        </>
    );
}

InterfaceUtilizationPanel.propTypes = panelPropTypes;

InterfaceUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'InterfaceUsage',
    catalogEntry: {
        category: PANEL_CATEGORIES.INTERFACE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Network utilization for a selected interface`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, interfaceFilterId],
        optional: [],
    },
})(InterfaceUtilizationPanel);
